import {
  Box,
  Columns,
  Container,
  Heading,
  Stack,
  Text,
  Visible,
} from '@spaceship-fspl/components';
import {
  Color,
  getSpace,
  match,
  paddingX,
  paddingY,
  ResponsiveValue,
} from '@spaceship-fspl/styles';
import { TrackingCategory } from '@spaceship-fspl/tracking';
import { MobileOptions } from 'components/mobile-options';
import {
  PerformanceCard,
  PerformanceCardProps,
} from 'components/performance-card';
import {
  SuperPrismicPortfolio,
  VoyagerPrismicPortfolio,
} from 'helpers/products';
import React from 'react';
import styled from 'styled-components';

import { PanelSection } from '../panel-section';

type PortfolioData<T> = Omit<PerformanceCardProps<T>, 'variant'> & {
  name: string;
};

export interface PortfolioPerformancePanelProps<T> {
  backgroundColor?: ResponsiveValue<Color>;
  title: string;
  disclaimer: React.ReactNode;
  portfolios?: Array<PortfolioData<T>>;
  selectedPortfolio: T;
  onPortfolioChange: (value: T) => void;
}

const StyledPortfolioContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  row-gap: ${getSpace('md')};
  ${paddingX({ xs: 'sm', xl: 'xxl' })};
  ${paddingY('lg')};
`;

const CardWrapper = styled.div<{ count: number }>`
  flex: 1 1 50%;
  max-width: 50%;

  ${match('xxl')`
    flex: 1 1 ${(props: { count: number }) => (props.count > 4 ? '33.333' : 100 / props.count)}%;
    max-width: ${(props: { count: number }) => (props.count > 4 ? '33.333' : 100 / props.count)}%;
  `}
`;

export function PortfolioPerformancePanel<
  T extends VoyagerPrismicPortfolio | SuperPrismicPortfolio,
>({
  title,
  disclaimer,
  portfolios,
  selectedPortfolio,
  onPortfolioChange,
  ...props
}: PortfolioPerformancePanelProps<T>): React.ReactElement {
  return (
    <TrackingCategory category="Performance Panel">
      <PanelSection {...props}>
        <Stack spaceY="lg">
          <Container>
            <Heading variant={2} align="center">
              {title}
            </Heading>
          </Container>

          {!!portfolios && portfolios.length > 0 && (
            <>
              <Visible isHidden={{ md: true }} displayValue="block">
                <MobileOptions
                  options={portfolios.map((portfolio) => ({
                    ...portfolio,
                    label: portfolio.name ?? '',
                    value: portfolio.type ?? '',
                  }))}
                  selectedOption={selectedPortfolio}
                  onChange={(value) => onPortfolioChange(value)}
                  variant="light"
                  fullHeight
                  renderOption={(props) => (
                    <Box paddingTop="sm" paddingBottom="md" height="100%">
                      <PerformanceCard variant="dark" {...props} />
                    </Box>
                  )}
                />
              </Visible>

              <Visible isHidden={{ xs: true, md: false }} displayValue="block">
                <StyledPortfolioContainer>
                  {portfolios.map((portfolio) => (
                    <CardWrapper key={portfolio.type} count={portfolios.length}>
                      <PerformanceCard {...portfolio} variant="dark" />
                    </CardWrapper>
                  ))}
                </StyledPortfolioContainer>
              </Visible>
            </>
          )}
          <Container>
            <Columns>
              <Columns.Column
                offset={{ xs: 0, lg: 1 / 12 }}
                width={{ xs: 1, lg: 10 / 12 }}
              >
                <Text
                  variant={3}
                  color="neutral.085"
                  align="center"
                  component="div"
                >
                  {disclaimer}
                </Text>
              </Columns.Column>
            </Columns>
          </Container>
        </Stack>
      </PanelSection>
    </TrackingCategory>
  );
}
